/**
  @module services
*/
import { Service, createService } from "app/utils/service";
import { fetchPost, fetchPut, fetchGet } from "app/utils/request";
import { reject } from "rsvp";

const PHONE_VERIFICATION_FAILURE_REASONS = {
  unprocessable_entity: "phone_verification_unprocessable_entity",
  unknown: "phone_verification_unknown",
};

/**
 * @class PhoneVerificationService
 *
 */
class PhoneVerificationService extends Service {
  static _name = "PhoneVerificationService";

  /**
   * Request a start of a phone verification.
   *
   * @param {string} phone
   * @param {string} phone_country
   * @returns {Promise}
   */
  verify(phone, phone_country) {
    return fetchPost(
      "verifications",
      { phone, phone_country },
      { version: 1 },
    ).catch((e) => handleErrors(PHONE_VERIFICATION_FAILURE_REASONS, e));
  }

  /**
   * Check if verification code can be resent
   *
   * @param {string} verification_id
   * @returns {Promise}
   */
  canResendCode(verification_id) {
    return fetchGet(`verifications/${verification_id}`, { version: 1 }).catch(
      (e) =>
        handleErrors(PHONE_VERIFICATION_FAILURE_REASONS, e).then((res) => {
          return res;
        }),
    );
  }

  /**
   * Poll canResendCode until canRetry is true (no max attempts limit)
   *
   * @param {string} verification_id
   * @returns {Promise<VerificationResponse>}
   */
  pollCanResendCode(verification_id) {
    const interval = 35000;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await this.canResendCode(verification_id);

          if (response && response.canRetry === true) {
            resolve(response);
            return;
          }

          console.log("[Service] cas false, scheduling next poll...");
          setTimeout(poll, interval);
        } catch (error) {
          console.error(`[Service] Error during polling:`, error);
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Request a confirmation of a phone verification with a code.
   *
   * @param {string} id
   * @param {string} code
   * @returns {Promise}
   */
  confirm(id, code) {
    return fetchPut("verifications/" + id, { code }, { version: 1 }).catch(
      (e) => handleErrors(PHONE_VERIFICATION_FAILURE_REASONS, e),
    );
  }
}

const handleErrors = (messageTypes, e) => {
  if (e.errors && e.errors.length) {
    return reject(
      messageTypes[e.originalErrors.status] || messageTypes["unknown"],
    );
  }
  return reject("unknown");
};

export default createService(PhoneVerificationService);
